import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * TARS Assistant runtime settings & shared state.
 *
 * Everything here lives 100% client-side. Only the numeric settings block is
 * sent along with each /api/chat request to the backend. TTS runs on the
 * browser via window.speechSynthesis, no external API.
 */

export const STORAGE_KEY = "bluebloodfr_tars_settings_v1";

export const DEFAULT_SETTINGS = {
  humour: 50,
  honesty: 50,
  brevity: 50,
  sarcasm: 50,
  technical_detail: 50,
  tts_enabled: false,
  voice_uri: "", // empty = let the browser pick a TARS-like voice
  voice_rate: 0.9, // 0.5 – 1.5   (deeper/slower feel, matches pyttsx3 rate=150)
  voice_pitch: 0.75, // 0 – 2      (deeper male-robot tone)
};

const NUMERIC_KEYS = [
  "humour",
  "honesty",
  "brevity",
  "sarcasm",
  "technical_detail",
];

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const sanitize = (raw) => {
  const merged = { ...DEFAULT_SETTINGS, ...(raw || {}) };
  NUMERIC_KEYS.forEach((k) => {
    const n = Number(merged[k]);
    merged[k] = Number.isFinite(n) ? clamp(Math.round(n), 0, 100) : DEFAULT_SETTINGS[k];
  });
  merged.tts_enabled = !!merged.tts_enabled;
  merged.voice_uri = typeof merged.voice_uri === "string" ? merged.voice_uri : "";
  const rate = Number(merged.voice_rate);
  merged.voice_rate = Number.isFinite(rate) ? clamp(rate, 0.5, 1.5) : 1.0;
  const pitch = Number(merged.voice_pitch);
  merged.voice_pitch = Number.isFinite(pitch) ? clamp(pitch, 0, 2) : 1.0;
  return merged;
};

const readInitial = () => {
  if (typeof window === "undefined") return { ...DEFAULT_SETTINGS };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    return sanitize(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
};

const TarsAssistantContext = createContext(null);

export const TarsAssistantProvider = ({ children }) => {
  const [settings, setSettings] = useState(readInitial);
  // Shared "assistant state" hint (idle / thinking / speaking / listening / configuring / error)
  const [assistantState, setAssistantState] = useState("idle");
  // Which surface owns the current speech (main | widget | null) — used to avoid
  // both instances speaking at the same time.
  const [speakingSurface, setSpeakingSurface] = useState(null);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      /* storage might be disabled — ignore */
    }
  }, [settings]);

  const updateSetting = useCallback((key, value) => {
    setSettings((prev) => sanitize({ ...prev, [key]: value }));
  }, []);

  const updateMany = useCallback((patch) => {
    setSettings((prev) => sanitize({ ...prev, ...patch }));
  }, []);

  const reset = useCallback(() => {
    setSettings({ ...DEFAULT_SETTINGS });
  }, []);

  const value = useMemo(
    () => ({
      settings,
      updateSetting,
      updateMany,
      reset,
      assistantState,
      setAssistantState,
      speakingSurface,
      setSpeakingSurface,
    }),
    [settings, updateSetting, updateMany, reset, assistantState, speakingSurface]
  );

  return (
    <TarsAssistantContext.Provider value={value}>
      {children}
    </TarsAssistantContext.Provider>
  );
};

export const useTarsAssistant = () => {
  const ctx = useContext(TarsAssistantContext);
  if (!ctx) {
    throw new Error(
      "useTarsAssistant must be used inside a <TarsAssistantProvider>"
    );
  }
  return ctx;
};

/**
 * Extract only the 5 numeric personality values that we send to the backend.
 * TTS-related fields stay 100% client-side.
 */
export const toBackendPayload = (settings) => ({
  humour: settings.humour,
  honesty: settings.honesty,
  brevity: settings.brevity,
  sarcasm: settings.sarcasm,
  technical_detail: settings.technical_detail,
});
